/**
 * 내 정보
 * ---------------------------------------------------------------------------
 * ## 왜 제품에 이 화면이 있나
 * WORK&Hub 콘솔은 **기업 관리자만** 들어간다(2026-10-27). 일지만 쓰는
 * 사람은 Hub 에 못 들어가므로, 여기에 자리가 없으면 비밀번호를 바꿀
 * 방법 자체가 없어진다.
 *
 * ## 이름과 이메일은 보여 주되 잠근다
 * 둘 다 WORK&Hub 가 원본이고, 이메일은 로그인 아이디이기도 하다.
 * 여기서 고쳐 봐야 다음 동기화 때 되돌아간다 — 바뀌지도 않을 값을
 * 바꿀 수 있는 것처럼 보여 주지 않는다. 대신 **어디서 바꾸는지**를 적는다.
 *
 * ## 값은 Hub 에 직접 묻는다
 * 제품 API 를 거치면 같은 값을 두 번 옮겨 적게 되고 한쪽이 낡는다.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { platform, type PlatformProfile, type PlatformSession } from '../lib/platform';
import { HelpH, HelpP, PageHelp } from '../components/PageHelp';

const { Text } = Typography;

/** 010-1234-5678 꼴로 다듬는다. 저장은 다듬은 값으로 한다. */
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export function MyAccountPage() {
  const { message, modal } = AntApp.useApp();
  const queryClient = useQueryClient();

  const [phone, setPhone] = useState('');
  const [pwForm] = Form.useForm<{
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>();

  const me = useQuery<PlatformProfile>({
    queryKey: ['platform', 'me'],
    queryFn: () => platform.me(),
  });

  const sessions = useQuery<PlatformSession[]>({
    queryKey: ['platform', 'sessions'],
    queryFn: () => platform.sessions(),
  });

  // 서버 값이 오면 입력칸을 맞춘다. 사용자가 고치는 중에는 건드리지 않는다.
  useEffect(() => {
    if (me.data) setPhone(me.data.phone ?? '');
  }, [me.data]);

  const savePhone = useMutation({
    mutationFn: () => platform.updatePhone(phone),
    onSuccess: async () => {
      message.success('연락처를 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['platform', 'me'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const changePassword = useMutation({
    mutationFn: async () => {
      const v = await pwForm.validateFields();
      await platform.changePassword(v.currentPassword, v.newPassword, v.confirmPassword);
    },
    onSuccess: () => {
      message.success('비밀번호를 바꿨습니다. 다른 기기는 다시 로그인해야 합니다.');
      pwForm.resetFields();
      void queryClient.invalidateQueries({ queryKey: ['platform', 'sessions'] });
    },
    onError: (e: Error) => {
      // 폼 검사 실패는 칸마다 빨간 글씨가 이미 떴다. 토스트까지 띄우지 않는다.
      if (e.message) message.error(e.message);
    },
  });

  const revoke = useMutation({
    mutationFn: (sessionId: string) => platform.revokeSession(sessionId),
    onSuccess: async () => {
      message.success('해당 기기의 로그인을 끊었습니다.');
      await queryClient.invalidateQueries({ queryKey: ['platform', 'sessions'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const p = me.data;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <PageHelp title="내 정보 — 화면 설명">
        <HelpP>
          내 연락처와 비밀번호, 지금 로그인되어 있는 기기를 관리합니다. 이 값들의 원본은{' '}
          <b>WORK&amp;Hub</b> 에 있어서, 여기서 바꾸면 WORK&amp;CRM 등 다른 제품에도 함께
          반영됩니다.
        </HelpP>
        <HelpH>이름과 이메일은 왜 못 바꾸나요</HelpH>
        <HelpP>
          두 값은 회사가 관리하는 인사 기록입니다. 이메일은 <b>로그인 아이디</b>이기도 해서,
          바꾸면 결재선·알림 같은 다른 기록과 어긋날 수 있습니다. 바꾸셔야 하면 회사의{' '}
          <b>기업 관리자</b>에게 요청해 주세요.
        </HelpP>
        <HelpH>비밀번호를 바꾸면</HelpH>
        <HelpP>
          지금 쓰고 계신 기기만 남고 <b>다른 기기는 모두 로그아웃</b>됩니다. 비밀번호가 샜을
          때 남의 접속을 한 번에 끊기 위해서입니다.
        </HelpP>
      </PageHelp>

      <Card size="small" title="내 정보" loading={me.isLoading}>
        {me.isError && (
          <Alert
            type="error"
            showIcon
            message="내 정보를 불러오지 못했습니다"
            description={(me.error as Error)?.message}
            style={{ marginBottom: 12 }}
          />
        )}

        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="이름">
            {p?.name || '-'}
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
              회사가 관리합니다. 바꾸시려면 기업 관리자에게 요청해 주세요.
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="이메일 (로그인 아이디)">
            {p?.email || '-'}
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
              로그인 아이디라 바꿀 수 없습니다.
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="회사">{p?.companyName || '-'}</Descriptions.Item>
          <Descriptions.Item label="부서">{p?.departmentName || '-'}</Descriptions.Item>
          <Descriptions.Item label="사번">{p?.empNo || '-'}</Descriptions.Item>
          <Descriptions.Item label="마지막 로그인">
            {p?.lastLoginAt ? new Date(p.lastLoginAt).toLocaleString('ko-KR') : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="연락처">
        <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
          <Input
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            placeholder="010-0000-0000"
            maxLength={13}
          />
          <Button
            type="primary"
            loading={savePhone.isPending}
            onClick={() => savePhone.mutate()}
          >
            저장
          </Button>
        </Space.Compact>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          비워 두고 저장하시면 연락처를 지웁니다. 결재·알림에서 담당자를 찾을 때 쓰입니다.
        </Text>
      </Card>

      <Card size="small" title="비밀번호 바꾸기">
        <Form
          form={pwForm}
          layout="vertical"
          style={{ maxWidth: 420 }}
          onFinish={() => changePassword.mutate()}
        >
          <Form.Item
            name="currentPassword"
            label="지금 비밀번호"
            rules={[{ required: true, message: '지금 쓰시는 비밀번호를 적어 주세요.' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="새 비밀번호"
            rules={[{ required: true, message: '새 비밀번호를 적어 주세요.' }]}
            /* 규칙은 Hub 가 판정한다. 여기서 또 적으면 두 곳이 어긋난다. */
            extra="회사의 비밀번호 규칙에 맞지 않으면 저장할 때 알려 드립니다."
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="새 비밀번호 확인"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '한 번 더 적어 주세요.' },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || getFieldValue('newPassword') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('새 비밀번호가 서로 다릅니다.')),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={changePassword.isPending}>
            비밀번호 바꾸기
          </Button>
        </Form>
      </Card>

      <Card size="small" title="로그인한 기기" loading={sessions.isLoading}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          모르는 기기가 있으면 끊어 주세요. 끊으면 그 기기는 즉시 로그아웃됩니다.
        </Text>
        <Table
          size="small"
          rowKey="sessionId"
          style={{ marginTop: 12 }}
          pagination={false}
          dataSource={sessions.data ?? []}
          columns={[
            {
              title: '기기',
              dataIndex: 'deviceLabel',
              render: (v: string | null, r: PlatformSession) => (
                <span>
                  {v || '알 수 없는 기기'}
                  {r.current && (
                    <Tag color="blue" style={{ marginLeft: 6 }}>
                      지금 이 기기
                    </Tag>
                  )}
                </span>
              ),
            },
            { title: 'IP', dataIndex: 'clientIp', width: 150 },
            {
              title: '마지막 활동',
              dataIndex: 'lastSeenAt',
              width: 180,
              render: (v: string) => new Date(v).toLocaleString('ko-KR'),
            },
            {
              title: '',
              width: 90,
              render: (_: unknown, r: PlatformSession) =>
                r.current ? null : (
                  <Button
                    size="small"
                    danger
                    loading={revoke.isPending}
                    onClick={() =>
                      modal.confirm({
                        title: '이 기기의 로그인을 끊을까요?',
                        content: '그 기기는 즉시 로그아웃되고 다시 로그인해야 합니다.',
                        okText: '끊기',
                        okButtonProps: { danger: true },
                        cancelText: '그만두기',
                        onOk: () => revoke.mutateAsync(r.sessionId).then(() => undefined),
                      })
                    }
                  >
                    끊기
                  </Button>
                ),
            },
          ]}
        />
      </Card>
    </Space>
  );
}
